import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { take } from 'rxjs';
import { SESSION_ID } from 'src/app/constants/authentication.constants';
import { splitFilename } from 'src/app/helpers/file-helpers';
import { CaseModel } from 'src/app/models/case.model';
import { DocumentModel } from 'src/app/models/document.model';
import { LoadingAction } from 'src/app/models/loading-action.model';
import { DocumentDataService } from 'src/app/services/document-data.service';
import { DocumentService } from 'src/app/services/document.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ToastService } from 'src/app/services/toast.service';
import { WindowService } from 'src/app/services/window.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'tx-upload-document',
  templateUrl: './upload-document.component.html',
  styleUrls: ['./upload-document.component.scss'],
})
export class UploadDocumentComponent implements OnInit {
  public document: DocumentModel;
  public preselectedContactId: string;
  public preselectedCaseId: string;
  public acceptedFormat: string = '.doc, .docx, .pdf';
  public submitUrl: string;
  public sessionId: string = SESSION_ID;
  private defaultSubmitUrl: string;
  private pdfSubmitUrl: string;
  @ViewChild('openInNewTabForm') openInNewTabForm!: ElementRef;
  @Output() uploadDocument: EventEmitter<DocumentModel> = new EventEmitter<DocumentModel>();

  constructor(
    private documentDataService: DocumentDataService,
    private documentService: DocumentService,
    private toastService: ToastService,
    private windowService: WindowService,
    private changeDetectorRef: ChangeDetectorRef,
    public loadingService: LoadingService
  ) {
    this.document = {
      isActiveDocument: false,
      saveAsPdf: false,
      answersChanged: false,
      openInAnotherSession: false,
      pdfAnswersChanged: false,
      txdocsAnswersChanged: false,
    };
    this.preselectedCaseId = this.documentDataService.getCasId();
    this.preselectedContactId = this.documentDataService.getCtcId();
    this.defaultSubmitUrl = environment.endpoints.apiUrl + '/document/opendocumentinnewsession';
    this.pdfSubmitUrl = environment.endpoints.apiUrl + '/pdfdocument/opendocumentinnewsession';
    this.submitUrl = this.defaultSubmitUrl;
  }

  ngOnInit() {
    if (this.preselectedCaseId && this.preselectedCaseId !== '') {
      this.document.casID = this.preselectedCaseId;
    }
  }

  selectFile(e: FileList) {
    const file = e.item(0);
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        const base64 = reader.result?.toString().replace(/^data:(.*,)?/, '') || ''; // remove file meta data from the string
        this.document.document = base64;
        this.document.filename = file.name;
      }
    };
  }

  onCaseSelected(event: CaseModel) {
    this.document.casID = event.casID;
    this.document.folder = event.folder;
    this.document.casDescription = event.description;
    this.document.ctcFirstName = event.ctcFirstName;
    this.document.ctcLastName = event.ctcLastName;
    //console.log(this.document);
  }

  onUploadClicked() {
    this.upload();
  }

  getTooltipMessage() {
    return `Please upload a document in a ${this.acceptedFormat} format.`;
  }

  private upload() {
    this.loadingService.loading = true;
    this.loadingService.loadingAction = LoadingAction.Uploading;
    this.documentService
      .saveDocument(this.document)
      .pipe(take(1)) //Automatic Unsubscribe after one received
      .subscribe({
        next: (res) => {
          if (res) {
            this.loadingService.loading = false;
            this.loadingService.loadingAction = LoadingAction.None;
            this.toastService.show('Document successfully uploaded', {
              classname: 'bg-success',
              autohide: true,
            });
            this.document = res;
            this.openUploadedDocumentInNewTab();
            this.uploadDocument.emit(this.document);
          }
        },
        error: (err) => {
          this.loadingService.loading = false;
          this.loadingService.loadingAction = LoadingAction.None;
          this.uploadDocument.emit(this.document);
        },
        complete: () => {},
      });
  }

  private openUploadedDocumentInNewTab() {
    if (this.document && this.document.dcmID && this.document.filename) {
      // set hidden form values for open in new tab functionality
      const dcmIdInput = document.getElementById('dcmID') as HTMLInputElement;
      if (dcmIdInput) {
        dcmIdInput.value = this.document.dcmID;
      }

      const htmlSessionId = document.getElementById(SESSION_ID) as HTMLInputElement;
      if (htmlSessionId) {
        htmlSessionId.value = this.windowService.getSessionIdFromWindow() ?? '';
      }

      const extension = splitFilename(this.document.filename).extension;
      this.submitUrl = extension.toLowerCase() === 'pdf' ? this.pdfSubmitUrl : this.defaultSubmitUrl;
      // force update of UI to update the submit url of the form
      this.changeDetectorRef.detectChanges();

      this.openInNewTabForm.nativeElement.submit();
    }
  }
}
